import { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to check freelance status and redirect if trial ended
 * @param {boolean} blockActions - If true, will redirect to choose-model page when trial ended
 * @returns {object} freelanceStatus object
 */
export const useFreelanceStatus = (blockActions = true) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [freelanceStatus, setFreelanceStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkFreelanceStatus = async () => {
      // Only check for freelancers
      if (!user?.is_freelance && user?.role !== 'freelance') {
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/api/freelance/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setFreelanceStatus(response.data);
        
        // Redirect to choose-model page if trial ended and blocking is enabled
        if (blockActions && response.data.needs_choice) {
          // Don't redirect if already on the choose-model page
          if (!location.pathname.includes('/freelance/choose-model')) {
            navigate('/freelance/choose-model', { replace: true });
          }
        }
      } catch (error) {
        console.error('Failed to check freelance status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkFreelanceStatus();
  }, [user, navigate, location.pathname, blockActions]);

  return {
    freelanceStatus,
    loading,
    isFreelance: freelanceStatus?.is_freelance || false,
    isTrial: freelanceStatus?.is_trial || false,
    trialEnded: freelanceStatus?.trial_ended || false,
    needsChoice: freelanceStatus?.needs_choice || false,
    daysRemaining: freelanceStatus?.days_remaining || 0,
    isRepresented: freelanceStatus?.is_represented || false,
    canPerformActions: freelanceStatus?.can_perform_actions !== false,
    canUndo: freelanceStatus?.can_undo || false,
    graceHoursRemaining: freelanceStatus?.grace_hours_remaining || 0,
    operatingPartnerId: freelanceStatus?.operating_partner_id
  };
};

/**
 * Hook to fetch freelance restrictions for UI display
 */
export const useFreelanceRestrictions = () => {
  const { user } = useContext(AuthContext);
  const [restrictions, setRestrictions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRestrictions = async () => {
      if (!user?.is_freelance) {
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/api/freelance/restrictions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRestrictions(response.data);
      } catch (error) {
        console.error('Failed to fetch freelance restrictions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRestrictions();
  }, [user]);

  return {
    restrictions,
    loading,
    canSendMessages: restrictions?.restrictions?.can_send_messages ?? true,
    canCreateProposals: restrictions?.restrictions?.can_create_proposals ?? true,
    canApplyToProjects: restrictions?.restrictions?.can_apply_to_projects ?? true,
    canViewClientContacts: restrictions?.restrictions?.can_view_client_contacts ?? true,
    canNegotiateRates: restrictions?.restrictions?.can_negotiate_rates ?? true,
    isRepresented: restrictions?.freelance_status === 'represented_free',
    operatingPartnerId: restrictions?.operating_partner_id
  };
};

/**
 * Check if a freelancer can send messages
 * @param {object} freelanceStatus - Status from useFreelanceStatus hook
 * @returns {boolean}
 */
export const canSendMessage = (freelanceStatus) => {
  if (!freelanceStatus?.is_freelance) return true;
  if (freelanceStatus.is_represented) return false;
  if (freelanceStatus.needs_choice) return false;
  return true;
};

/**
 * Check if a freelancer can initiate commercial actions
 * @param {object} freelanceStatus - Status from useFreelanceStatus hook
 * @returns {boolean}
 */
export const canInitiateCommercialAction = (freelanceStatus) => {
  return canSendMessage(freelanceStatus);
};

export default useFreelanceStatus;
